import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography } from '@mui/material';
import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { insuranceActions, insuranceSelector } from '../../../model';
import { useMutation, useApolloClient, useQuery } from '@apollo/client';
import { CAMUNDA_START_PROCESS } from '../../../api';
import { QUERY_PROPOSALS } from 'queries/common';
import {
  AftermarketProductVendorApiChannel,
  Proposal,
  ProposalMenuOption,
  ProposalProduct,
} from '@trnsact/trnsact-shared-types';
import { Card } from 'modules/desking/ui/components/proposalProductsCards/products/gaig/Card';
import { GET_PROPOSAL_PRODUCTS } from 'modules/desking/api';
import { ProposalProductsResponse } from 'modules/desking/api/types';
import { ProposalProductCardModes } from 'modules/desking/types';

export const SendInsuranceQuoteDialog = () => {
  const dispatch = useDispatch();
  const client = useApolloClient();

  const [productToUseForInsuranceExpress, setProductToUseForInsuranceExpress] = useState<ProposalProduct | null>(null);
  const [insuranceExpressProductHasPricing, setInsuranceExpressProductHasPricing] = useState(false);

  const { loading: aftermarketProductDataLoading } = useQuery<ProposalProductsResponse>(GET_PROPOSAL_PRODUCTS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        sourceOfCreation: ['base', 'manual'],
        proposalProductCriteria: {},
      },
    },
    onCompleted: response => {
      console.log('aftermarketProductData', response);
      const p =
        response.proposalProducts.find(p => {
          return (
            p.aftermarketProduct?.aftermarketVendorApiChannel === AftermarketProductVendorApiChannel.Gaig &&
            p.aftermarketProduct?.externalId === 'GAIG'
          );
        }) || null;
      if (!p?.aftermarketProduct?.config) {
        return;
      }
      const next = {
        ...p,
        aftermarketProduct: {
          ...p.aftermarketProduct,
          config: {
            ...p.aftermarketProduct.config,
            productConfig: {
              ...(p.aftermarketProduct.config?.productConfig ?? {}),
              state: creditApplication?.state ?? '',
              postalCode: creditApplication?.postalCode ?? '',
            },
          },
        },
      };
      setProductToUseForInsuranceExpress(next);
    },
    onError: error => {
      console.error('Error fetching aftermarket product data:', error);
    },
  });

  const { isOpen, data, handleClose } = useModal(ModalsKeys.SendInsuranceQuoteDialog);
  const [createProposalLoading, setCreateProposalLoading] = useState<boolean>(false);
  const [proposalCreatedSuccessfully, setProposalCreatedSuccessfully] = useState<boolean>(false);

  const insuranceProposals = useSelector(insuranceSelector.proposals);
  const creditApplication = useSelector(insuranceSelector.creditApplication);
  const vendorOpportunity = useSelector(insuranceSelector.vendorOpportunity);
  const account = useSelector((state: any) => state.account);

  const pollingAttempts = useRef(0);
  const originalProposalCount = useRef(insuranceProposals.length);

  const equipmentCost =
    creditApplication &&
    Array.isArray(creditApplication.equipments) &&
    creditApplication.equipments.length > 0 &&
    creditApplication.equipments[0]
      ? (creditApplication.equipments[0].retailPrice as number)
      : creditApplication?.amountRequested || undefined;

  const [startSendInsuranceExpressQuote, { loading }] = useMutation(CAMUNDA_START_PROCESS, {
    onCompleted: () => {
      pollForNewProposals();
    },
  });

  const pollForNewProposals = async () => {
    setCreateProposalLoading(true);
    const interval = setInterval(async () => {
      pollingAttempts.current += 1;
      console.log(`Polling attempt ${pollingAttempts.current}`);

      try {
        const { data } = await client.query<{ proposals: Proposal[] }>({
          query: QUERY_PROPOSALS,
          variables: { voId: vendorOpportunity?.vendorOpportunityId },
          fetchPolicy: 'network-only',
        });

        const proposals = data?.proposals || [];
        const insuranceProposals = proposals.filter(p => {
          const options = p.lenderPaymentOptions?.[0]?.paymentCriteria?.menu?.menuOptions || [];
          return options.some((mo: ProposalMenuOption) => {
            return mo?.products.some(
              (product: any) =>
                product?.aftermarketProduct?.config?.productConfig?.GAIG_QUOTE !== undefined ||
                product?.aftermarketProduct?.config?.productConfig?.GAIGQUOTE !== undefined
            );
          });
        });

        if (insuranceProposals.length > originalProposalCount.current) {
          clearInterval(interval);
          dispatch(insuranceActions.setProposals(insuranceProposals.map(p => ({ ...p, id: p.id || '' }))));
          setCreateProposalLoading(false);
          setProposalCreatedSuccessfully(true);
          return;
        }

        if (pollingAttempts.current >= 8) {
          clearInterval(interval);
          setCreateProposalLoading(false);
        }
      } catch (err) {
        console.error('Error polling proposals:', err);
        clearInterval(interval);
        setCreateProposalLoading(false);
      }
    }, 5000);
  };

  const handleSubmit = () => {
    pollingAttempts.current = 0;
    originalProposalCount.current = insuranceProposals.length;
    startSendInsuranceExpressQuote({
      variables: {
        input: {
          processKey: 'insurance-express-create-insurance-quote',
          creditApplicationId: creditApplication?.id,
          dynamicsId: creditApplication?.dynamicsId,
          vendorAccountId: account.id,
          extraData: {
            productToUseForInsuranceExpress: productToUseForInsuranceExpress,
          },
        },
      },
    });
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Typography component="span" variant="h6">
          Send Insurance Quote
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {proposalCreatedSuccessfully && (
          <Typography variant="body1" color="success.main" sx={{ marginBottom: '20px' }}>
            Insurance quote has been sent successfully!
          </Typography>
        )}
        {(aftermarketProductDataLoading || createProposalLoading || !productToUseForInsuranceExpress) && (
          <>
            {aftermarketProductDataLoading ? (
              <>
                <Typography>Gathering Insurance Products...</Typography>{' '}
                <LinearProgress sx={{ marginBottom: '20px' }} />{' '}
              </>
            ) : null}
            {createProposalLoading ? (
              <>
                <Typography>Creating Insurance Quote...</Typography>{' '}
                <LinearProgress sx={{ marginBottom: '20px' }} />{' '}
              </>
            ) : null}
          </>
        )}
        {!proposalCreatedSuccessfully &&
          !aftermarketProductDataLoading &&
          !createProposalLoading &&
          productToUseForInsuranceExpress && (
            <div>
              <Card
                mode={ProposalProductCardModes.Desking}
                prisingConfig={{
                  isCostDisabled: true,
                  isMarkupDisabled: true,
                  isRateDisabled: true,
                  isRetailDisabled: true,
                  isShowCost: true,
                  isShowMarkup: false,
                  isShowRate: false,
                  isShowRetail: false,
                }}
                updateProductConfiguration={data => {
                  if (data.retailCost !== undefined && data.retailCost > 0) {
                    setInsuranceExpressProductHasPricing(true);
                  } else {
                    setInsuranceExpressProductHasPricing(false);
                  }
                  setProductToUseForInsuranceExpress((prev: any) => {
                    return prev
                      ? {
                          ...prev,
                          aftermarketProduct: {
                            ...prev.aftermarketProduct,
                            config: {
                              ...prev.aftermarketProduct?.config,
                              productConfig: {
                                ...prev.aftermarketProduct?.config?.productConfig,
                                ...data,
                              },
                            },
                          },
                        }
                      : null;
                  });
                }}
                product={productToUseForInsuranceExpress}
                productConfiguration={productToUseForInsuranceExpress.aftermarketProduct?.config?.productConfig || {}}
                products={[productToUseForInsuranceExpress]}
                equipmentCost={equipmentCost}
              />
              <Typography variant="body2">
                Clicking "Send Insurance Quote" will send an insurance quote to the customer.
                <br />
                Ensure that the equipment on the opportunity is correct and up to date.
              </Typography>
            </div>
          )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{proposalCreatedSuccessfully ? 'Close' : 'Cancel'}</Button>
        <Button
          disabled={proposalCreatedSuccessfully || !insuranceExpressProductHasPricing}
          loading={createProposalLoading || loading}
          onClick={handleSubmit}
          variant="contained"
        >
          Send Insurance Quote
        </Button>
      </DialogActions>
    </Dialog>
  );
};
